import {
  PhoneIncoming,
  PhoneOutgoing,
  MessageSquareMore,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

export type Direction = 'inbound' | 'outbound';
export type Channel = 'voice' | 'sms' | 'email' | 'portal';

export interface DirectionIndicatorProps {
  /** Direction of the interaction */
  direction: Direction | string | null | undefined;
  /** Channel type (affects icon choice) */
  channel?: Channel | string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show the label text */
  showLabel?: boolean;
  /** Custom class name */
  className?: string;
}

// =============================================================================
// Size configurations
// =============================================================================

const sizeConfig = {
  sm: {
    container: 'w-6 h-6',
    icon: 'h-3 w-3',
    text: 'text-xs',
  },
  md: {
    container: 'w-8 h-8',
    icon: 'h-4 w-4',
    text: 'text-sm',
  },
  lg: {
    container: 'w-10 h-10',
    icon: 'h-5 w-5',
    text: 'text-base',
  },
};

// =============================================================================
// Component
// =============================================================================

/**
 * Reusable component for displaying inbound/outbound direction.
 * Use this component across the product for consistent direction indicators.
 *
 * @example
 * // Voice call with icon only
 * <DirectionIndicator direction="inbound" channel="voice" />
 *
 * @example
 * // SMS with label
 * <DirectionIndicator direction="outbound" channel="sms" showLabel />
 *
 * @example
 * // Generic direction arrow (no channel)
 * <DirectionIndicator direction="inbound" showLabel />
 */
export function DirectionIndicator({
  direction,
  channel,
  size = 'md',
  showLabel = false,
  className,
}: DirectionIndicatorProps): React.ReactElement {
  const isInbound = direction === 'inbound';
  const config = sizeConfig[size];

  // Get the appropriate icon based on channel and direction
  const getIcon = () => {
    if (channel === 'voice') {
      return isInbound ? PhoneIncoming : PhoneOutgoing;
    }
    if (channel === 'sms' || channel === 'email') {
      return MessageSquareMore;
    }
    // Generic direction arrow for other channels
    return isInbound ? ArrowDownLeft : ArrowUpRight;
  };

  const Icon = getIcon();
  const label = isInbound ? 'Inbound' : 'Outbound';

  // Color scheme: green for inbound, blue for outbound
  const colorClass = isInbound ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600';

  if (showLabel) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div
          className={cn(
            'rounded-lg flex items-center justify-center',
            config.container,
            colorClass
          )}
        >
          <Icon className={config.icon} />
        </div>
        <span className={cn(config.text, 'text-gray-600')}>{label}</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg flex items-center justify-center',
        config.container,
        colorClass,
        className
      )}
    >
      <Icon className={config.icon} />
    </div>
  );
}

/**
 * Simple inline direction label without icon.
 * Use for compact displays where only text is needed.
 */
export function DirectionLabel({
  direction,
  className,
}: {
  direction: Direction | string | null | undefined;
  className?: string;
}): React.ReactElement {
  const isInbound = direction === 'inbound';
  return (
    <span className={cn('text-xs', isInbound ? 'text-green-600' : 'text-blue-600', className)}>
      {isInbound ? 'Inbound' : 'Outbound'}
    </span>
  );
}
